export const black = '#000000';
export const white = '#ffffff';

export const gray = '#999999';
export const lightGray = '#cccccc';
export const gray30 = '#4d4d4d';
export const gray70 = '#b3b3b3';
export const gray85 = '#d9d9d9';
export const gray90 = '#e6e6e6';
export const gray95 = '#f2f2f2';
export const gray97 = '#f7f7f7';
export const lightShadeGray = '#e6e6e6';
export const secondaryLightGray = '#f8f5fc';
export const approxSilver = '#bfbfbf';

// Green family
export const green = '#3dc90a';
export const congLuGreen = '#0abf89';
export const deepSpringGreen = '#00ffb2';
export const softSpringGreen = '#00f2a9';

// Purple family
export const purple = '#6f2ee6';
export const softPurple = '#f8f5fc';

// Red family
export const persianRed = '#d32f2f';
export const softRed = '#ed5c5c';

// White family
export const antiFlashWhite = '#f1f1f1';
export const secondaryWhite = '#fcfaeb';
export const yellow = '#f5cc03';

// ── Brand palette (rebrand foundation) ──
// New brand tokens added alongside the legacy `purple`. Screens migrate onto
// these incrementally; nothing below remaps existing consumers.
export const ink = '#101626'; // primary text / dominant surface
export const royal = '#4664f9'; // primary CTA
export const royalHover = '#2f4fe8';
export const periwinkle = '#90a2fb'; // borders on soft surfaces
export const lavender = '#b5c1fd'; // deeper soft surface
export const mist = '#dae0fe'; // soft tint surface
export const indigo = '#3146ae'; // deep accent
export const lime = '#aabf00'; // gain / buy / bullish
export const limeBg = '#f0fbec';
export const limeText = '#7d8c00'; // gain text on light surfaces (lime fails contrast)
export const coral = '#ff899d'; // loss / sell / bearish / error
export const coralBg = '#fef2f2';
export const cyan = '#7de6f4'; // instrument accent
export const amber = '#ffb600'; // news accent / medium impact
export const amberBg = '#fef6ec';
export const mint = '#cbf5fb'; // light-blue button accent
export const dusk = '#4a5f81';
export const rankSilver = '#cccccc'; // leaderboard 2nd-place accent
export const rankBronze = '#cf8c55'; // leaderboard 3rd-place accent
export const gray40 = '#666666';
export const gray80 = '#cccccc'; // idle control borders (checkboxes, steppers)
export const grayFC = '#fcfcfc';

/** Connection-status palette (AQT-888); every slot is mode-dependent. */
const connection = {
  warn: 'var(--conn-warn)',
  reconnectingBg: 'var(--conn-reconnecting-bg)',
  reconnectingBorder: 'var(--conn-reconnecting-border)',
  reconnectingDetail: 'var(--conn-reconnecting-detail)',
  offlineBg: 'var(--conn-offline-bg)',
  stroke: 'var(--conn-stroke)',
  recoveredBg: 'var(--conn-recovered-bg)',
  recoveredBorder: 'var(--conn-recovered-border)',
  recoveredDetail: 'var(--conn-recovered-detail)',
  staleText: 'var(--conn-stale-text)',
  chipDisabledBorder: 'var(--conn-chip-disabled-border)',
  chipDisabledText: 'var(--conn-chip-disabled-text)',
  sellDisabledBg: 'var(--conn-sell-disabled-bg)',
  sellDisabledText: 'var(--conn-sell-disabled-text)',
  buyDisabledBg: 'var(--conn-buy-disabled-bg)',
  buyDisabledText: 'var(--conn-buy-disabled-text)',
} as const;

// Brand palette — kept in parity across web Theme.ts / iOS Color+Utils / Android colors.xml.
// Mode-dependent slots resolve to CSS custom properties declared in GlobalStyle
// (:root dark / :root[data-theme="light"]); mode-agnostic slots stay literal.
export const brand = {
  accentCyan: 'var(--accent-ink-cyan)',
  accentInk: 'var(--accent-ink)',
  accentWash: 'var(--accent-wash)',
  accentWashStrong: 'var(--accent-wash-strong)',
  alabasterGray: '#f6f6f6',
  alphaIndigo: '#8d85fb',
  aquaCyan: '#7de5f3',
  bearish: 'var(--bearish)',
  bgBase: 'var(--bg-base)',
  bgDeep: 'var(--bg-deep)',
  bullish: 'var(--bullish)',
  buyGreen: 'var(--buy-green)',
  buyGreenBg: 'var(--buy-green-bg)',
  carbonBlack: '#000000',
  // Categorical data-viz palette (allocation donut, multi-series charts). Mode-agnostic
  // mid-tones from the brand spectrum so slices stay distinct on light AND dark surfaces.
  chartCategorical: ['#4664F9', '#7DE5F3', '#8D85FB', '#2BB3D4', '#FFC65C', '#90A2FB', '#3DDC97', '#FF7DA1'],
  connection,
  fontSize: {
    body: '14px',
    bodyLg: '16px',
    button: '13px',
    buttonSm: '11px',
    caption: '12px',
    display: '56px',
    feedName: '15px',
    feedNameLg: '16.5px',
    feedNameSm: '12px',
    feedText: '15px',
    feedTextLg: '17px',
    feedTextSm: '12.5px',
    h1: '40px',
    h2: '28px',
    h3: '22px',
    h4: '18px',
    micro: '10.5px',
    newsTitle: '15px',
    pairName: '26px',
    sectionTitle: '21px',
    stat: '17px',
  },
  fontWeight: {
    body: 300,
    bold: 700,
    display: 800,
    extrabold: 800,
    heading: 500,
    label: 600,
    light: 300,
    medium: 500,
    regular: 400,
    semibold: 600,
  },
  glowBuy: 'var(--glow-buy)',
  glowCobalt: 'var(--glow-cobalt)',
  glowCyan: 'var(--glow-cyan)',
  glowDanger: 'var(--glow-danger)',
  glowIndigo: 'var(--glow-indigo)',
  glowIndigoLg: 'var(--glow-indigo-lg)',
  glowSell: 'var(--glow-sell)',
  glowTeal: 'var(--glow-teal)',
  gradBrand: 'linear-gradient(45deg, #4664f9 0%, #8d85fb 100%)',
  gradBuy: 'linear-gradient(180deg, #3de0a6 0%, #1fa97a 100%)',
  gradCard: 'var(--grad-card)',
  gradChart: 'linear-gradient(90deg, #4664f9 0%, #7de5f3 100%)',
  gradCobalt: 'linear-gradient(180deg, #5470ff 0%, #3d55e8 100%)',
  gradCta: 'linear-gradient(180deg, #7a7dff 0%, #4655f0 100%)',
  gradDanger: 'linear-gradient(180deg, #FF5E78 0%, #E0294A 100%)',
  gradHeroCard: 'var(--grad-hero-card)',
  gradHeroSilver: 'var(--grad-hero-silver)',
  gradHeroText: 'var(--grad-hero-text)',
  gradTeal: 'linear-gradient(180deg, #2bb3d4 0%, #137d9b 100%)',
  headerBg: 'var(--header-bg)',
  letterSpacing: {
    heading: '0.06em',
    normal: '0.04em',
    wide: '0.14em',
    wider: '0.22em',
  },
  lineHeight: {
    body: 1.5,
    caption: 1.35,
    display: 1.05,
    h1: 1.1,
    h2: 1.15,
    h3: 1.2,
    h4: 1.3,
  },
  radii: {
    '2xl': '30px',
    lg: '18px',
    md: '14px',
    pill: '999px',
    sm: '10px',
    xl: '24px',
    xs: '6px',
  },
  royalCobalt: '#4664f9',
  royalCobaltHover: '#2f4fe8',
  searchBg: 'var(--search-bg)',
  sellRed: 'var(--sell-red)',
  sellRedBg: 'var(--sell-red-bg)',
  shadowCard: 'var(--shadow-card)',
  space: {
    '1': '4px',
    '10': '72px',
    '2': '8px',
    '3': '12px',
    '4': '16px',
    '5': '20px',
    '6': '24px',
    '7': '32px',
    '8': '40px',
    '9': '56px',
  },
  strokeCyan: 'var(--stroke-cyan)',
  strokeMed: 'var(--stroke-med)',
  strokeSoft: 'var(--stroke-soft)',
  strokeStrong: 'var(--stroke-strong)',
  surface1: 'var(--surface-1)',
  surface2: 'var(--surface-2)',
  surface3: 'var(--surface-3)',
  surfaceGlass: 'var(--surface-glass)',
  textDisabled: 'var(--text-disabled)',
  textPrimary: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  textTertiary: 'var(--text-tertiary)',
  tradeTeal: 'var(--trade-teal)',
  warning: 'var(--warning)',
  warningBg: 'var(--warning-bg)',
} as const;

// ── Scales ──
const fonts = {
  sans: "'Axiforma', system-ui, -apple-system, sans-serif",
  display: "'Axiforma', system-ui, -apple-system, sans-serif",
  druk: "'DrukWide', 'Axiforma', sans-serif", // ultra-wide; opt-in for marketing/hero surfaces
  mono: "'DepartureMono', ui-monospace, 'Courier New', monospace",
};

const space = {
  xxxs: 2,
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 28,
  xxxl: 32,
  xxxxl: 40,
  xxxxxl: 68,
};

const radii = { sm: 6, md: 10, lg: 16, xl: 24 };

const shadows = {
  card: '0 1px 0 rgba(16, 22, 38, 0.04), 0 8px 24px -12px rgba(16, 22, 38, 0.08)',
};

const responsiveBreakpoints = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1440, xxl: 1920 } as const;

const zIndex = {
  button: 1,
  sticky: 1,
  chat: 50,
  dropDown: 60,
  sideBar: 60,
  sideBarHeader: 88,
  subHeader: 89,
  header: 90,
  hover: 100,
  tooltip: 101,

  // index with specific component
  channelSearch: -1,
  dateRangePickerDrag: -1,
  welcomeBackground: -1,

  previewImage: 0,

  avatarContainer: 1,
  chatFollowedTraderLabel: 1,
  chatSimpleStatus: 1,
  confetti: 1,
  dayTimePicker: 1,
  memberOnly: 1,
  groupAvatarPriority: 1,
  timePickerContainer: 1,

  createChannelHeader: 2,
  chatSearchContainer: 2,
  luckyDrawButton: 2,
  goToTop: 2,
  tradeMenu: 2,

  select: 9,

  floatingMenu: 10,

  hamburgerMenu: 999,

  createChatHeader: 1000,

  chatInnerModal: 9999,

  modal: 10000,
  hoverTip: 10000,

  downloadAppModal: 10001,

  // Above every modal — snackbars are fired from inside modals (kopy trade, position close).
  toast: 10002,
} as const;

const breakpoints = {
  xs: `${responsiveBreakpoints.xs}px`,
  sm: `${responsiveBreakpoints.sm}px`,
  md: `${responsiveBreakpoints.md}px`,
  lg: `${responsiveBreakpoints.lg}px`,
  xl: `${responsiveBreakpoints.xl}px`,
  xxl: `${responsiveBreakpoints.xxl}px`,
};

const device = {
  mobile: `max-width: ${responsiveBreakpoints.sm - 1}px`,
  tablet: `max-width: ${responsiveBreakpoints.md - 1}px`,
  laptop: `max-width: ${responsiveBreakpoints.lg - 1}px`,
  desktop: `max-width: ${responsiveBreakpoints.xl - 1}px`,
  desktopXl: `max-width: ${responsiveBreakpoints.xxl - 1}px`,
};

const fontSizes = {
  h1: {
    lg: 30,
    md: 26,
    sm: 24,
    xl: 40,
  },
  h2: {
    lg: 24,
    md: 22,
    sm: 20,
  },
  h3: {
    lg: 28,
    md: 22,
    sm: 19,
  },
  h4: {
    lg: 22,
    md: 18,
    sm: 17,
    xs: 15,
  },
  h5: {
    lg: 16,
    md: 14,
    sm: 12,
  },
  lg: 18,
  md: 16,
  sm: 14,
  xl: 20,
  xs: 12,
} as const;

const commonStyle = {
  black,
  white,
  purple,
  green,
  gray,
  lightGray,
  gray90,
  gray95,
  gray97,
  softRed,
  softPurple,
  secondaryWhite,
  yellow,
  // brand palette
  ink,
  royal,
  royalHover,
  periwinkle,
  lavender,
  mist,
  indigo,
  lime,
  limeBg,
  limeText,
  coral,
  coralBg,
  cyan,
  amber,
  amberBg,
  mint,
  dusk,
  rankSilver,
  rankBronze,
  gray40,
  gray80,
  grayFC,
  brand,
  // scales
  fonts,
  space,
  radii,
  shadows,
  breakpoints,
  device,
  fontFamily: 'Axiforma',
  fontSizes,
  zIndex,
};

const kycTheme = {
  background: white,
  breakpoints: {
    xs: breakpoints.xs,
    sm: breakpoints.sm,
    md: breakpoints.md,
    lg: breakpoints.lg,
    xl: breakpoints.xl,
    xxl: breakpoints.xxl,
  },
  device: {
    mobile: `max-width: ${responsiveBreakpoints.sm - 1}px`,
    tablet: `max-width: ${responsiveBreakpoints.md - 1}px`,
    laptop: `max-width: ${responsiveBreakpoints.lg - 1}px`,
    desktop: `max-width: ${responsiveBreakpoints.xl - 1}px`,
    desktopXl: `max-width: ${responsiveBreakpoints.xxl - 1}px`,
  },
  button: {
    active: { primary: congLuGreen, secondary: gray70, off: approxSilver },
    background: { primary: deepSpringGreen, secondary: gray95, off: lightShadeGray },
    border: { on: black, off: gray },
    boxShadow: { on: black, off: gray },
    disabled: { background: lightGray, border: gray, boxShadow: gray },
    hover: { primary: softSpringGreen, secondary: lightShadeGray, off: gray85 },
    text: { on: black, off: gray },
  },
  border: lightShadeGray,
  gray: gray,
  primaryPurple: purple,
  text: black,
  radioButton: {
    color: purple,
    backgroundColor: white,
    hover: { opacity: 0.4 },
    borderColor: lightShadeGray,
  },
  textField: {
    accentColor: purple,
    focusColor: purple,
    errorColor: persianRed,
    backgroundColor: {
      disabled: antiFlashWhite,
      active: gray97,
    },
    caretColor: purple,
  },
  errorMessage: {
    fontColor: persianRed,
  },
  checkBox: {
    backgroundColor: purple,
  },
  select: {
    backgroundColor: gray97,
  },
  progress: {
    on: purple,
    off: gray85,
  },
};

export const Theme = {
  ...commonStyle,
  accountBalance: {
    button: {
      background: { normal: royal, hover: royalHover, active: indigo },
      text: white,
    },
    disabled: { background: gray95, text: gray },
    text: black,
  },
  accountSettings: {
    list: {
      background: secondaryWhite,
      color: purple,
    },
    textField: {
      background: gray95,
    },
    warning: softRed,
  },
  alertBanner: {
    closeButton: black,
    background: secondaryWhite,
    text: black,
  },
  arrowButton: {
    background: purple,
  },
  background: white,
  border: gray90,
  borderDark: '#979797',
  button: {
    active: { off: '#bfbfbf', primary: '#0abf89', secondary: '#b3b3b3' },
    background: { off: gray90, primary: '#00ffb2', secondary: gray95 },
    border: { off: gray, on: black },
    boxShadow: { off: gray, on: black },
    disabled: { background: lightGray, border: gray, boxShadow: gray },
    hover: { off: '#d9d9d9', primary: '#00f2a9', secondary: gray90 },
    text: { off: gray, on: black },
  },
  buttonText: white,
  buy: green,
  chat: {
    background: brand.bgBase,
    channel: {
      active: brand.accentWash,
      activeBorder: brand.royalCobalt,
      hover: brand.surface3,
      readLabel: brand.textPrimary,
      timeLabel: brand.textTertiary,
      timeLabelUnread: brand.royalCobalt,
      unreadBadge: brand.royalCobalt,
    },
    input: {
      activeBackground: brand.surface2,
      idleBackground: brand.surface1,
    },
    message: {
      background: brand.surface2,
      backgroundMe: brand.gradCobalt,
      border: brand.strokeSoft,
      text: brand.textPrimary,
      textMe: '#ffffff',
    },
    deleted: {
      background: brand.surface2,
      color: brand.textTertiary,
    },
  },
  campaigns: {
    marketingCampaign: {
      banner: {
        title: {
          top: '#88ff00',
          bottom: '#00ffb2',
        },
        text: white,
        countdown: {
          background: '#2b2608',
          border: yellow,
          text: yellow,
        },
      },
      mission: {
        amount: purple,
        credit: green,
      },
      luckyDraw: {
        boxCount: yellow,
      },
      label: gray,
      slogan: yellow,
    },
  },
  checkbox: ink,
  datePicker: {
    lightText: '#f0f8ff',
    primaryBackground: ink,
    secondaryBackground: mist,
  },
  dateRangePicker: {
    backgroundColor: gray97,
  },
  dayTimePicker: {
    backgroundColor: gray97,
  },
  deposit: { input: { background: gray97, placeholder: gray }, text: black },
  disabled: white,
  error: {
    fontColor: 'red',
  },
  feedback: {
    imageBackground: gray97,
  },
  fileUploader: {
    border: '#dfdce5',
    background: softPurple,
  },
  headerBorder: gray95,
  hover: secondaryWhite,
  iconButton: {
    active: { background: '#b3b3b3' },
    background: gray95,
    border: black,
    disabled: { background: lightGray },
    hover: { background: gray90 },
    onClick: '#d9d9d9',
    text: black,
    tooltip: {
      background: black,
      text: white,
    },
  },
  imageBackground: gray97,
  impact: {
    selectBackground: gray97,
    high: { color: coral, background: coralBg },
    mid: { color: amber, background: amberBg },
    low: { color: limeText, background: limeBg },
  },
  input: {
    background: gray95,
    warning: softRed,
  },
  lightGray: lightGray,
  link: purple,
  liveAccountRegistration: kycTheme,
  maxWidth: '1680px',
  menu: {
    active: purple,
    background: '#f7f7f5',
    liveAccountBackground: purple,
  },
  personalCorner: {
    trendingForYou: {
      newsIndexBackground: ink,
      newsIndexColor: white,
    },
    welcomeMessage: {
      background: ink,
      greetingColor: white,
      tradeLinkColor: mint,
    },
  },
  priceDown: brand.sellRed,
  priceUp: brand.buyGreen,
  priceZero: ink,
  primaryPurple: purple,
  promotionBanner: {
    arrow: {
      background: 'rgba(181, 193, 253, 0.6)', // lavender @ 60%
      hover: lavender,
    },
    pagination: {
      inactive: 'rgba(181, 193, 253, 0.6)', // lavender @ 60%
      active: white,
    },
  },
  promotionModal: {
    slider: {
      active: ink,
      inactive: gray90,
    },
  },
  purple,
  select: {
    background: gray97,
  },
  selectBox: {
    background: softPurple,
  },
  sell: softRed,
  sentiment: {
    background: gray95,
    bearishBackground: coralBg,
    bearishText: coral,
    bullishBackground: limeBg,
    bullishText: limeText,
    text: gray,
  },
  sideMenuItem: {
    selectBackgroundColor: purple,
    unSelectBackgroundColor: gray95,
  },
  signUp: {
    active: purple,
    text: black,
    warn: softRed,
  },
  signUpLiveAcc: {
    background: ink,
    text: white,
  },
  sltpTrack: {
    background: mist,
    rail: gray80,
    tooltip: { background: ink, text: white },
    track: { lose: coral, win: lime, pending: gray },
  },
  softPurple,
  softRed,
  subMenu: {
    activeMobile: softPurple,
    background: '#eeebf2',
    icon: '#979797',
    selectedText: purple,
    text: black,
  },
  text: black,
  title: ink,
  unread: purple,
  user: {
    administrator: {
      background: gray97,
      tagText: purple,
      text: gray,
    },
  },
  upgradeLiveAccount: {
    background: indigo,
  },
  warning: {
    fontColor: softRed,
    background: '#fdeeee',
  },
  watchListMenu: {
    backgroundColor: gray95,
  },
  widgetLine: { default: amber, discover: '#ff811a', myTrade: '#1e5ef6', trade: '#00dbb2' },
};

export type AppTheme = typeof Theme;
